import type { Recipe } from '@recipes/contracts';
import { Alert } from '@recipes/ui';
import { ExternalLink, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { currentFormatLocale } from '@/lib/i18n';

/** Prominent attribution for imported content and the AI-generated label (spec 6.3, 7.6). */
export function SourceBox({ recipe }: { recipe: Pick<Recipe, 'origin' | 'source'> }) {
  const { t } = useTranslation();
  const source = recipe.source;
  if (recipe.origin === 'AI_GENERATED' || source?.type === 'AI') {
    return (
      <Alert variant="info" hideIcon data-testid="source-box">
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 size-4 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">{t('recipes.source.aiTitle')}</p>
            <p className="text-muted-foreground">{t('recipes.source.aiHint')}</p>
          </div>
        </div>
      </Alert>
    );
  }
  if (!source || (recipe.origin === 'USER' && !source.url && !source.attribution && !source.author))
    return null;
  return (
    <div className="rounded-lg border bg-muted/40 p-3 text-sm" data-testid="source-box">
      <p className="font-medium">{t('recipes.source.title')}</p>
      <dl className="mt-1 grid gap-x-4 gap-y-0.5 sm:grid-cols-[auto_1fr]">
        {(source.siteName || source.domain) && (
          <>
            <dt className="text-muted-foreground">{t('recipes.source.site')}</dt>
            <dd>{source.siteName ?? source.domain}</dd>
          </>
        )}
        {source.author && (
          <>
            <dt className="text-muted-foreground">{t('recipes.source.author')}</dt>
            <dd>{source.author}</dd>
          </>
        )}
        {source.originalTitle && source.originalTitle !== '' && (
          <>
            <dt className="text-muted-foreground">{t('recipes.source.originalTitle')}</dt>
            <dd>{source.originalTitle}</dd>
          </>
        )}
        {source.url && (
          <>
            <dt className="text-muted-foreground">{t('recipes.source.link')}</dt>
            <dd>
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1 break-all text-primary underline-offset-4 hover:underline"
              >
                {source.canonicalUrl ?? source.url}
                <ExternalLink className="size-3.5 shrink-0" aria-hidden />
                <span className="sr-only">({t('recipes.source.opensNewTab')})</span>
              </a>
            </dd>
          </>
        )}
        {source.attribution && (
          <>
            <dt className="text-muted-foreground">{t('recipes.source.attribution')}</dt>
            <dd>{source.attribution}</dd>
          </>
        )}
        {source.importedAt && (
          <>
            <dt className="text-muted-foreground">{t('recipes.source.importedAt')}</dt>
            <dd>
              <time dateTime={source.importedAt}>
                {new Date(source.importedAt).toLocaleDateString(currentFormatLocale())}
              </time>
            </dd>
          </>
        )}
      </dl>
    </div>
  );
}
